import type { VisualizationDTO } from "@zplanem/responses"
import { useState } from "react"
import { ConfirmDeleteDialog } from "../../components/confirm-delete-dialog"
import { useVisualizationMutations } from "../../hooks/use-visualizations"
import { getTranslations } from "../../i18n"
import { useLocale } from "../../locale-context"

function DownloadIcon() {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			className="w-4 h-4"
			aria-hidden="true"
		>
			<path d="M12 3v12m0 0 4-4m-4 4-4-4" />
			<path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
		</svg>
	)
}

function TrashIcon() {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			className="w-4 h-4"
			aria-hidden="true"
		>
			<path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2m2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
		</svg>
	)
}

function PlaceholderIcon({ kind }: { kind: VisualizationDTO["kind"] }) {
	if (kind === "url") {
		return (
			<svg
				viewBox="0 0 24 24"
				fill="none"
				stroke="currentColor"
				strokeWidth="1.5"
				className="w-8 h-8"
				aria-hidden="true"
			>
				<path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1" />
				<path d="M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1" />
			</svg>
		)
	}
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.5"
			className="w-8 h-8"
			aria-hidden="true"
		>
			<path d="M14 3v5h5" />
			<path d="M5 3h9l5 5v13a0 0 0 0 1 0 0H5a0 0 0 0 1 0 0V3Z" />
		</svg>
	)
}

/**
 * One grid cell: a rectangular thumbnail, the title below, the description as a
 * hover tooltip, and (on hover, top-left) up to two transparent-gray action
 * buttons — Download (file kinds) + Delete (creator only, with confirm).
 */
export function VisualizationCard({
	visualization,
	propertyId,
	projectId,
	onOpen,
}: {
	visualization: VisualizationDTO
	propertyId: string
	projectId: string
	onOpen: () => void
}) {
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const { remove } = useVisualizationMutations(propertyId, projectId)
	const [confirming, setConfirming] = useState(false)
	const isFile = visualization.kind !== "url"
	const label = visualization.title ?? ""
	// A url visualization's title is null only until enrichment finishes (the
	// task always fills at least a host fallback), so a null url title means
	// "still processing". Otherwise show the title if present — never a
	// placeholder when it's genuinely absent.
	const isProcessing = !visualization.title && visualization.kind === "url"
	const titleText =
		visualization.title ??
		(isProcessing ? t.projects.visualizations.processing : null)
	// Uploader name shown bottom-right of the thumbnail (ZPL-265). AI uploads show
	// the assistant label (matches the comment-author convention).
	const uploaderName =
		visualization.uploadedBy.actorType === "ai"
			? "Mareczek"
			: visualization.uploadedBy.displayName

	return (
		<div className="group relative">
			<button
				type="button"
				onClick={onOpen}
				title={visualization.description ?? undefined}
				className="block w-full cursor-pointer text-left"
			>
				<div className="relative aspect-[4/3] rounded-xl overflow-hidden border border-border bg-muted/40">
					{visualization.thumbnailUrl ? (
						<img
							src={visualization.thumbnailUrl}
							alt={label}
							loading="lazy"
							className="w-full h-full object-cover"
						/>
					) : (
						<div className="w-full h-full flex items-center justify-center text-muted-foreground/70">
							<PlaceholderIcon kind={visualization.kind} />
						</div>
					)}
					{uploaderName ? (
						// Decorative — the name is redundant over the thumbnail and would
						// otherwise pollute the open-button's accessible name.
						<div
							aria-hidden="true"
							title={t.projects.visualizations.uploadedByLabel.replace(
								"{name}",
								uploaderName,
							)}
							className="absolute right-1.5 bottom-1.5 max-w-[85%] truncate rounded-md bg-black/55 px-1.5 py-0.5 text-[11px] font-medium text-white backdrop-blur-sm opacity-100 transition-opacity md:opacity-0 md:group-hover:opacity-100"
						>
							{uploaderName}
						</div>
					) : null}
				</div>
				{titleText ? (
					<p
						className={
							isProcessing
								? "mt-1.5 px-0.5 text-sm font-medium italic text-muted-foreground truncate"
								: "mt-1.5 px-0.5 text-sm font-medium text-foreground truncate"
						}
					>
						{titleText}
					</p>
				) : null}
			</button>

			<div className="absolute left-2 top-2 flex gap-1.5 opacity-100 transition-opacity md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100">
				{isFile && visualization.sourceUrl ? (
					<a
						href={visualization.sourceUrl}
						target="_blank"
						rel="noopener noreferrer"
						aria-label={t.projects.visualizations.download}
						onClick={(e) => e.stopPropagation()}
						className="cursor-pointer rounded-md bg-black/50 hover:bg-black/70 text-white p-1.5 backdrop-blur-sm"
					>
						<DownloadIcon />
					</a>
				) : null}
				{visualization.canDelete ? (
					<button
						type="button"
						aria-label={t.projects.visualizations.delete}
						onClick={(e) => {
							e.stopPropagation()
							setConfirming(true)
						}}
						className="cursor-pointer rounded-md bg-black/50 hover:bg-black/70 text-white p-1.5 backdrop-blur-sm"
					>
						<TrashIcon />
					</button>
				) : null}
			</div>

			{confirming ? (
				<ConfirmDeleteDialog
					title={t.projects.visualizations.deleteTitle}
					body={t.projects.visualizations.deleteBody}
					confirmLabel={t.projects.visualizations.delete}
					cancelLabel={t.projects.visualizations.cancel}
					pending={remove.isPending}
					onCancel={() => setConfirming(false)}
					onConfirm={async () => {
						await remove.mutateAsync(visualization.id)
						setConfirming(false)
					}}
				/>
			) : null}
		</div>
	)
}
